package it.neckar.lizergy.model.financial.quote

import it.neckar.commons.tags.Tag
import it.neckar.commons.tags.Tags
import it.neckar.lizergy.model.financial.currency.Money
import it.neckar.lizergy.model.financial.currency.PriceWithProfit
import it.neckar.lizergy.model.financial.currency.ValueAddedTax
import it.neckar.lizergy.model.financial.currency.times
import it.neckar.lizergy.model.financial.quote.query.Query
import it.neckar.open.kotlin.lang.percent
import it.neckar.open.unit.currency.EUR
import it.neckar.open.unit.other.pct
import it.neckar.uuid.NativeUuid
import it.neckar.uuid.randomForEnvironment
import it.neckar.uuid.randomUuid4
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Duration
import kotlin.time.Duration.Companion.hours
import kotlin.uuid.Uuid

/**
 * Represents an item that has a concrete price ("leaf" of the tree)
 */
@Serializable
@SerialName("QuoteItem")
data class QuoteItem(
  override val uuid: NativeUuid,

  /**
   * The (bold) headline
   */
  override val headline: String,
  /**
   * The details - might be a long text describing in detail
   */
  override val details: String?,

  /**
   * The price for *one* item.
   * Is multiplied with [amount]
   */
  val priceForOneElement: PriceWithProfit,

  /**
   * The amount
   */
  override val amount: Amount,

  override val factor: @pct Double = 100.percent,

  override val visibility: Visibility,

  /**
   * The tags this item is relevant for.
   *
   * Can be used to calculate sums / discounts etc.
   */
  val relevance: Tags,

  /**
   * If an item is optional
   */
  override val optionality: Optionality = Optionality.Mandatory,

  /**
   * VAT percentage ("Mehrwertsteuer-Satz")
   */
  val valueAddedTax: ValueAddedTax = ValueAddedTax.nineteenPercent,

  ) : QuoteElement {

  constructor(
    headline: String,
    details: String?,
    priceForOneElement: PriceWithProfit,
    amount: Amount,
    factor: @pct Double,
    visibility: Visibility,
    relevance: Tags,
    optionality: Optionality = Optionality.Mandatory,
    valueAddedTax: ValueAddedTax = ValueAddedTax.nineteenPercent,
  ) : this(
    uuid = Uuid.randomForEnvironment(),
    headline = headline,
    details = details,
    priceForOneElement = priceForOneElement,
    amount = amount,
    factor = factor,
    visibility = visibility,
    relevance = relevance,
    optionality = optionality,
    valueAddedTax = valueAddedTax,
  )

  init {
    //The old default value for an empty quote item is no longer allowed
    if (headline == "<-empty->") {
      throw IllegalStateException("Invalid headline: [$headline]")
    }
  }

  val sellingPrice: @EUR Money
    get() = priceForOneElement.sellingPrice * amount

  val priceWithProfit: @EUR PriceWithProfit
    get() = PriceWithProfit(
      inputPrice = priceForOneElement.inputPrice * amount,
      sellingPrice = netPrice,
    )

  override val netPrice: @EUR Money
    get() = sellingPrice.timesAndRound(factor)

  val vatPrice: @EUR Money
    get() = netPrice * valueAddedTax

  val grossPrice: @EUR Money
    get() = netPrice + vatPrice

  val discount: @EUR Money
    get() = netPrice - sellingPrice

  override fun relevanceOrNull(): Tags {
    return relevance
  }

  override fun queryQuoteElement(query: Query): QuoteItem? {
    return if (query.matches(this)) this else null
  }

  override fun listQuoteItems(query: Query): List<QuoteItem> {
    return if (query.matches(this)) listOf(this) else emptyList()
  }

  override fun vats(query: Query): List<ValueAddedTax> {
    return if (query.matches(this)) listOf(valueAddedTax) else emptyList()
  }

  override fun workingTimes(query: Query): Duration {
    return amount.let { if (it.unit == AmountUnit.Hours) it.amount.hours else Duration.ZERO }
  }

  fun isRelevantFor(tag: Tag): Boolean = this.relevance.contains(tag)


  companion object {
    /**
     * Empty quote item - visibility is public
     */
    fun createEmpty(uuid: Uuid = randomUuid4()): QuoteItem = QuoteItem(uuid, "", "", PriceWithProfit.Zero, Amount(1), 100.percent, Visibility.Public, Tags.empty, Optionality.Optional)
  }
}
